'use client'

import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from 'react'

interface CustomScrollViewProps {
  children: React.ReactNode
  // 滚动条轨道样式
  scrollbarTrackStyle?: React.CSSProperties
  // 滚动条滑块样式
  scrollbarThumbStyle?: React.CSSProperties
  // 是否显示滚动条
  showScrollbar?: boolean
  // 容器样式
  style?: React.CSSProperties
  // 内容容器样式
  contentContainerStyle?: React.CSSProperties
  // 自定义类名
  className?: string
}

export interface CustomScrollViewRef {
  scrollTo: (options: { x?: number; y?: number; animated?: boolean }) => void
  scrollToEnd: (options?: { animated?: boolean }) => void
}

const CustomScrollView = forwardRef<CustomScrollViewRef, CustomScrollViewProps>(
  (
    {
      children,
      scrollbarTrackStyle,
      scrollbarThumbStyle,
      showScrollbar = true,
      style,
      contentContainerStyle,
      className,
    },
    ref
  ) => {
    const scrollViewRef = useRef<HTMLDivElement>(null)
    const [scrollIndicatorHeight, setScrollIndicatorHeight] = useState(0)
    const [scrollIndicatorTop, setScrollIndicatorTop] = useState(0)

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
      scrollTo: (options: { x?: number; y?: number; animated?: boolean }) => {
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollTo({
            left: options.x,
            top: options.y,
            behavior: options.animated ? 'smooth' : 'auto',
          })
        }
      },
      scrollToEnd: (options?: { animated?: boolean }) => {
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollTo({
            top: scrollViewRef.current.scrollHeight,
            behavior: options?.animated ? 'smooth' : 'auto',
          })
        }
      },
    }))

    // 更新滚动条位置
    const updateScrollbar = useCallback(() => {
      if (!scrollViewRef.current) return

      const scrollElement = scrollViewRef.current
      const contentHeight = scrollElement.scrollHeight
      const scrollViewHeight = scrollElement.clientHeight
      const scrollY = scrollElement.scrollTop

      // 如果内容不需要滚动，不显示滚动条
      if (contentHeight <= scrollViewHeight) {
        setScrollIndicatorHeight(0)
        return
      }

      // 计算滚动条基础高度
      const baseIndicatorHeight = Math.max(
        (scrollViewHeight / contentHeight) * scrollViewHeight,
        20 // 最小高度
      )

      const maxScrollDistance = contentHeight - scrollViewHeight

      // 处理过度滚动（Web环境下一般不支持过度滚动，但保留逻辑以防万一）
      if (scrollY < 0) {
        // 向上过度滚动 - 压缩滚动条顶部
        const overScroll = Math.abs(scrollY)
        const compressionRatio = Math.max(0.5, 1 - overScroll / 100)
        const compressedHeight = baseIndicatorHeight * compressionRatio
        setScrollIndicatorHeight(compressedHeight)
        setScrollIndicatorTop(0)
      } else if (scrollY > maxScrollDistance) {
        // 向下过度滚动 - 压缩滚动条底部
        const overScroll = scrollY - maxScrollDistance
        const compressionRatio = Math.max(0.5, 1 - overScroll / 100)
        const compressedHeight = baseIndicatorHeight * compressionRatio
        const indicatorTop = scrollViewHeight - compressedHeight
        setScrollIndicatorHeight(compressedHeight)
        setScrollIndicatorTop(indicatorTop)
      } else {
        // 正常滚动
        const scrollRatio = Math.min(scrollY / maxScrollDistance, 1)
        const maxIndicatorMove = scrollViewHeight - baseIndicatorHeight
        const indicatorTop = scrollRatio * maxIndicatorMove
        setScrollIndicatorHeight(baseIndicatorHeight)
        setScrollIndicatorTop(indicatorTop)
      }
    }, [])

    // 处理滚动事件
    const handleScroll = useCallback(() => {
      updateScrollbar()
    }, [updateScrollbar])

    // 监听内容尺寸变化
    useEffect(() => {
      if (!scrollViewRef.current) return

      // 初始化滚动条
      const timer = setTimeout(() => {
        updateScrollbar()
      }, 100)

      // 创建 ResizeObserver 监听内容尺寸变化
      const resizeObserver = new ResizeObserver(() => {
        updateScrollbar()
      })

      resizeObserver.observe(scrollViewRef.current)

      return () => {
        clearTimeout(timer)
        resizeObserver.disconnect()
      }
    }, [updateScrollbar])

    return (
      <div
        className={className}
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          ...style,
        }}
      >
        <div
          ref={scrollViewRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            // 隐藏原生滚动条
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            ...contentContainerStyle,
          }}
          className="hide-scrollbar"
        >
          {children}
        </div>

        {/* 自定义滚动条 */}
        {showScrollbar && scrollIndicatorHeight > 0 && (
          <div
            style={{
              position: 'absolute',
              right: 1,
              top: 0,
              bottom: 0,
              width: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 1.5,
              ...scrollbarTrackStyle,
            }}
          >
            <div
              style={{
                width: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                borderRadius: 1.5,
                height: scrollIndicatorHeight,
                transform: `translateY(${scrollIndicatorTop}px)`,
                transition: 'transform 0.1s ease-out',
                ...scrollbarThumbStyle,
              }}
            />
          </div>
        )}

        {/* 隐藏webkit滚动条的样式 */}
        <style jsx>{
          /* i18n-ignore */ `
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `
        }</style>
      </div>
    )
  }
)

CustomScrollView.displayName = 'CustomScrollView'

export default CustomScrollView
